import { c as _c } from "react/compiler-runtime";
import { differenceInDays } from 'date-fns';
import { Formik } from 'formik';
import { useContext, useState } from 'react';
import { useLocation } from 'react-router';
import { Route, Switch as SwitchRoute, useHistory, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import useSWR from 'swr';
import { Grid, GridUnit } from '@hookdeck/theme';
import { PaymentElement } from '@stripe/react-stripe-js';
import { organization_levels_by_role } from '../../../../../../../../domain/roles';
import { APISubscription } from '../../../../../../../../typings/Subscription.interface';
import HookdeckAPI from '../../../../../client/Hookdeck';
import Sentry from '../../../../../client/Sentry';
import LINKS from '../../../../../configs/links';
import { OrbPlan } from '../../../../../typings/Orb.interface';
import { pluralize } from '../../../../../utils';
import { formatDate } from '../../../../../utils/date';
import { showChat } from '../../../../../utils/liveChat';
import Alert from '../../../../common/base/Alert';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import Container from '../../../../common/base/Container';
import Divider from '../../../../common/base/Divider';
import Icon from '../../../../common/base/Icon';
import Link from '../../../../common/base/Link';
import Loading from '../../../../common/base/Loading';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import { useDialog } from '../../../../common/Dialog';
import CheckboxInput from '../../../../common/Form/Fields/CheckboxInput';
import Switch from '../../../../common/Form/Fields/Switch';
import TextInput from '../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import Modal from '../../../../common/Modal';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { UserContext } from '../../../../contexts/UserContext';
import useSearchQuery from '../../../../hooks/useSearchQuery';
import { useDatePreferences } from '../../../../hooks/useDatePreferences';
import { DashboardContext } from '../../DashboardContext';
import WrapWithStripeElement from './WrapWithStripeElement';
const product_configs = {
  event_gateway: {
    title: 'Choose a Hookdeck Event Gateway plan',
    base_path: '/settings/organization/plans',
    pricing_link: LINKS.pricing,
    getPlans: (HookdeckAPI: HookdeckAPI) => HookdeckAPI.billing.getPlans(),
    getSubscriptionDetails: (HookdeckAPI: HookdeckAPI) => HookdeckAPI.billing.getSubscriptionDetails(),
    changePlan: (HookdeckAPI: HookdeckAPI, plan: string) => HookdeckAPI.billing.changePlan(plan),
    cancelPlan: (HookdeckAPI: HookdeckAPI) => HookdeckAPI.billing.cancel(),
    deriveSubscriptionState: subscription => ({
      plan: subscription.plan,
      cancel_at: subscription.cancel_at,
      features: subscription.features
    }),
    free_plan: {
      name: 'Developer',
      code_name: 'free'
    },
    post_upgrade_modal_fields: {
      starter: [{
        name: 'retention',
        label: 'Longer retention period'
      }, {
        name: 'usage_limit',
        label: 'Receive more then 10k request/events'
      }, {
        name: 'users',
        label: 'Add team members'
      }, {
        name: 'integrations',
        label: 'Enable integrations'
      }],
      growth: [{
        name: 'sla',
        label: 'Service uptime and latency SLA'
      }, {
        name: 'retention',
        label: 'Longer retention period'
      }, {
        name: 'sso',
        label: 'SSO & SCIM support'
      }, {
        name: 'metrics',
        label: 'Access to metrics export'
      }, {
        name: 'roles',
        label: 'Improved roles management with view-only role'
      }]
    },
    features: [{
      key: 'retention_days',
      label: 'Retention',
      format: value => `${value} ${pluralize(Number(value), 'Days')}`
    }, {
      key: 'max_users',
      label: '# of Users',
      format: (value, features) => features?.includes('unlimited_seats') ? 'Unlimited' : `${value} ${pluralize(Number(value), 'User')}`
    }, {
      key: 'all_integrations',
      label: 'Integrations (ie: Slack)',
      format: (value, features) => features?.includes('all_integrations') || false
    }, {
      key: 'metrics_export',
      label: 'Metrics Export (ie. Datadog)',
      format: (value, features) => features?.includes('metrics') || false
    }, {
      key: 'sla',
      label: 'Uptime & Latency SLA',
      format: (value, features) => {
        return features?.includes('sla') || false;
      }
    }, {
      key: 'viewer_role',
      label: 'Read-Only (Viewer) Role',
      format: (value, features) => features?.includes('viewer_role') || false
    }]
  },
  outpost: {
    title: 'Choose a Hookdeck Outpost plan',
    base_path: '/settings/organization/outpost/plans',
    pricing_link: LINKS.outpost.pricing,
    getPlans: (HookdeckAPI: HookdeckAPI) => HookdeckAPI.billing.getOutpostPlans(),
    getSubscriptionDetails: (HookdeckAPI: HookdeckAPI) => HookdeckAPI.billing.getOutpostSubscriptionDetails(),
    changePlan: (HookdeckAPI: HookdeckAPI, plan: string) => HookdeckAPI.billing.changeOutpostPlan(plan),
    cancelPlan: (HookdeckAPI: HookdeckAPI) => HookdeckAPI.billing.cancelOutpost(),
    deriveSubscriptionState: subscription => ({
      plan: subscription.outpost_plan,
      cancel_at: subscription.outpost_cancel_at,
      features: subscription.outpost_config?.features || []
    }),
    free_plan: {
      name: 'Starter',
      code_name: 'outpost_starter'
    },
    post_upgrade_modal_fields: {
      starter: [{
        name: 'retention',
        label: 'Longer retention period'
      }]
    },
    features: [{
      key: 'retention_days',
      label: 'Retention',
      format: value => `${value} ${pluralize(Number(value), 'Days')}`
    }, {
      key: 'sla',
      label: 'Uptime & Latency SLA',
      format: (value, features) => features?.includes('sla') || false
    }, {
      key: 'viewer_role',
      label: 'Read-Only (Viewer) Role',
      format: (value, features) => features?.includes('viewer_role') || false
    }, {
      key: 'sso',
      label: 'SSO (SAML/OIDC) & SCIM',
      format: (value, features) => features?.includes('sso') || false
    }, {
      key: 'multi_region',
      label: 'Multi-region (EU, US, APAC)',
      format: (value, features) => features?.includes('multi_region') || false
    }]
  }
};
const CancelModal: React.FC<{
  product: keyof typeof product_configs;
}> = t0 => {
  const $ = _c(22);
  const {
    product
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    mutateSubscription,
    teams
  } = useContext(DashboardContext);
  const history = useHistory();
  const {
    addToast
  } = useToasts();
  let t1;
  if ($[0] !== HookdeckAPI || $[1] !== product) {
    t1 = () => product_configs[product].getSubscriptionDetails(HookdeckAPI);
    $[0] = HookdeckAPI;
    $[1] = product;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: subscription_details
  } = useSWR(`${product}:subscription_details`, t1);
  let t2;
  if ($[3] !== HookdeckAPI || $[4] !== addToast || $[5] !== history || $[6] !== mutateSubscription || $[7] !== product || $[8] !== subscription_details || $[9] !== teams || $[10] !== timezone) {
    const teams_with_addons = teams?.filter(_temp) || [];
    const has_addons_enabled = teams_with_addons.length > 0;
    let t3;
    if ($[12] !== history || $[13] !== product) {
      t3 = () => history.push({
        pathname: product_configs[product].base_path,
        state: {
          scroll: false
        }
      });
      $[12] = history;
      $[13] = product;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    const onClose = t3;
    let t4;
    if ($[15] !== HookdeckAPI || $[16] !== addToast || $[17] !== history || $[18] !== mutateSubscription || $[19] !== product) {
      t4 = () => {
        product_configs[product].cancelPlan(HookdeckAPI).then(subscription => {
          mutateSubscription(subscription);
          history.push({
            pathname: "/settings/organization/billing",
            state: {
              scroll: false
            }
          });
          addToast("success", `You will be downgraded to the ${product_configs[product].free_plan.name} plan at the end of your billing period.`);
        }).catch(() => {
          addToast("error", "Failed to cancel plan, please contact us.");
        });
      };
      $[15] = HookdeckAPI;
      $[16] = addToast;
      $[17] = history;
      $[18] = mutateSubscription;
      $[19] = product;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    const onCancel = t4;
    let t5;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Button outline={true} icon="chat" onClick={showChat}>Contact us</Button>;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    t2 = <Modal title="Cancel your plan" cancel_label="Cancel" hide_close={true} submit_label="Confirm" footer_content={t5} onSubmit={onCancel} onCancel={onClose} onClose={onClose}>{!subscription_details ? <Loading /> : <><Text>You will be{" "}<strong>downgraded to the {product_configs[product].free_plan.name} (Free) Plan</strong>{" "}at the end of your billing period on{" "}<strong>{formatDate(subscription_details.current_billing_period_end_date, "MMM d, yyyy", timezone)}</strong>.</Text>{has_addons_enabled && <Alert warning={true} inline={true} m={{
          t: 4
        }}><Text as="p" m={0}><strong>Addons will be disabled</strong> for the following project{teams_with_addons.length > 1 ? "s" : ""}:{" "}{teams_with_addons.map(_temp2).join(", ")}.</Text><Text as="p" m={{
            t: 2,
            b: 0
          }} muted={true} size="s">Static IPs will be disabled for{" "}{teams_with_addons.length === 1 ? "this project" : "these projects"}.</Text></Alert>}</>}</Modal>;
    $[3] = HookdeckAPI;
    $[4] = addToast;
    $[5] = history;
    $[6] = mutateSubscription;
    $[7] = product;
    $[8] = subscription_details;
    $[9] = teams;
    $[10] = timezone;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
const ChangePlanConfirmationModal: React.FC<{
  product: keyof typeof product_configs;
  handlePostUpgrade: (sub: APISubscription) => void;
}> = t0 => {
  const $ = _c(37);
  const {
    product,
    handlePostUpgrade
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription
  } = useContext(DashboardContext);
  const history = useHistory();
  const {
    addToast
  } = useToasts();
  const {
    plan
  } = useParams();
  const [is_submitting, setIsSubmitting] = useState(false);
  let t1;
  if ($[0] !== HookdeckAPI || $[1] !== product) {
    t1 = () => product_configs[product].getPlans(HookdeckAPI);
    $[0] = HookdeckAPI;
    $[1] = product;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: plans
  } = useSWR(`${product}:plans`, t1);
  let t2;
  if ($[3] !== HookdeckAPI || $[4] !== product) {
    t2 = () => product_configs[product].getSubscriptionDetails(HookdeckAPI);
    $[3] = HookdeckAPI;
    $[4] = product;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data: subscription_details
  } = useSWR(`${product}:subscription_details`, t2);
  let t3;
  if ($[6] !== history || $[7] !== product) {
    t3 = () => history.push({
      pathname: product_configs[product].base_path,
      state: {
        scroll: false
      }
    });
    $[6] = history;
    $[7] = product;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onClose = t3;
  let is_current_plan_legacy;
  let t4;
  let target_plan;
  let target_plan_name;
  if ($[9] !== plan || $[10] !== plans || $[11] !== product || $[12] !== subscription || $[13] !== subscription_details) {
    const subscription_state = product_configs[product].deriveSubscriptionState(subscription);
    target_plan = plans?.find(p => p.external_plan_id === plan);
    const current_plan = plans?.find(p_0 => subscription_state.plan && subscription_state.plan.startsWith(p_0.external_plan_id));
    is_current_plan_legacy = !!subscription_details && !!current_plan && current_plan.version !== subscription_details.plan.version;
    target_plan_name = target_plan?.name.split(" ")[0];
    t4 = current_plan?.name.split(" ")[0];
    $[9] = plan;
    $[10] = plans;
    $[11] = product;
    $[12] = subscription;
    $[13] = subscription_details;
    $[14] = is_current_plan_legacy;
    $[15] = t4;
    $[16] = target_plan;
    $[17] = target_plan_name;
  } else {
    is_current_plan_legacy = $[14];
    t4 = $[15];
    target_plan = $[16];
    target_plan_name = $[17];
  }
  const current_plan_name = t4;
  let t5;
  if ($[18] !== HookdeckAPI || $[19] !== addToast || $[20] !== handlePostUpgrade || $[21] !== history || $[22] !== plan || $[23] !== product) {
    t5 = async () => {
      setIsSubmitting(true);
      ;
      try {
        const card = await HookdeckAPI.billing.getCard();
        if (card) {
          const new_subscription = await product_configs[product].changePlan(HookdeckAPI, plan);
          handlePostUpgrade(new_subscription);
        } else {
          history.push({
            pathname: `${product_configs[product].base_path}/${plan}`,
            state: {
              scroll: false
            }
          });
        }
      } catch (t6) {
        const error = t6;
        console.error(error);
        Sentry.captureException(error);
        addToast("error", "Something went wrong, please try again or contact us.");
        setIsSubmitting(false);
      }
    };
    $[18] = HookdeckAPI;
    $[19] = addToast;
    $[20] = handlePostUpgrade;
    $[21] = history;
    $[22] = plan;
    $[23] = product;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const onConfirm = t5;
  let t6;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Button outline={true} icon="chat" onClick={showChat}>Contact us</Button>;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const t7 = !target_plan;
  let t8;
  if ($[26] !== current_plan_name || $[27] !== is_current_plan_legacy || $[28] !== plans || $[29] !== target_plan_name) {
    t8 = !plans ? <Loading /> : current_plan_name ? <Text>You are changing from the{" "}<strong>{current_plan_name} Plan {is_current_plan_legacy ? " (legacy)" : ""}</strong>{" "}to the <strong>{target_plan_name} Plan</strong>.</Text> : <Text>You are changing to the <strong>{target_plan_name} Plan</strong>.</Text>;
    $[26] = current_plan_name;
    $[27] = is_current_plan_legacy;
    $[28] = plans;
    $[29] = target_plan_name;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== is_submitting || $[32] !== onClose || $[33] !== onConfirm || $[34] !== t7 || $[35] !== t8) {
    t9 = <Modal title="Confirm your plan change" cancel_label="Cancel" hide_close={true} submit_label="Confirm" footer_content={t6} onSubmit={onConfirm} onCancel={onClose} onClose={onClose} is_submitting={is_submitting} submit_disabled={t7}>{t8}</Modal>;
    $[31] = is_submitting;
    $[32] = onClose;
    $[33] = onConfirm;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
};
const PaymentMethodModal: React.FC<{
  product: keyof typeof product_configs;
  handlePostUpgrade: (sub: APISubscription) => void;
}> = t0 => {
  const $ = _c(19);
  const {
    product,
    handlePostUpgrade
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    user
  } = useContext(UserContext);
  const {
    addToast
  } = useToasts();
  const history = useHistory();
  const {
    plan
  } = useParams();
  let t1;
  if ($[0] !== history || $[1] !== product) {
    t1 = () => history.push({
      pathname: product_configs[product].base_path,
      state: {
        scroll: false
      }
    });
    $[0] = history;
    $[1] = product;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onClose = t1;
  let t2;
  if ($[3] !== HookdeckAPI || $[4] !== addToast || $[5] !== handlePostUpgrade || $[6] !== plan || $[7] !== product) {
    t2 = async (confirmCardSetup, values) => {
      ;
      try {
        const payment_method_id = await confirmCardSetup();
        await HookdeckAPI.billing.setPaymentMethodAsDefault(payment_method_id);
        await HookdeckAPI.billing.updateBillingEmail(values.email);
        await new Promise(_temp3);
        await product_configs[product].changePlan(HookdeckAPI, plan).catch(async () => {
          await new Promise(_temp4);
          return product_configs[product].changePlan(HookdeckAPI, plan);
        }).then(subscription => {
          handlePostUpgrade(subscription);
        });
      } catch (t3) {
        const err = t3;
        console.error(err);
        Sentry.captureException(err);
        addToast("error", "Something went wrong, please try again or contact us.");
      }
    };
    $[3] = HookdeckAPI;
    $[4] = addToast;
    $[5] = handlePostUpgrade;
    $[6] = plan;
    $[7] = product;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleSubmit = t2;
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Div flex={{
      align: "center",
      justify: "center"
    }} p={{
      t: 8,
      x: 8
    }}><Loading /></Div>;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== onClose) {
    t4 = <Modal title="Set your payment method" cancel_label="Cancel" hide_close={true} submit_label="Confirm & Pay" portal={true} onCancel={onClose} onClose={onClose}>{t3}</Modal>;
    $[10] = onClose;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== handleSubmit || $[13] !== onClose || $[14] !== user.email) {
    t5 = t6 => {
      const {
        confirmCardSetup: confirmCardSetup_0
      } = t6;
      return <Formik initialValues={{
        email: user.email,
        cc_complete: false
      }} onSubmit={values_0 => handleSubmit(confirmCardSetup_0, values_0)} validate={_temp5}>{props => <form onSubmit={props.handleSubmit}><Modal title="Set your payment method" cancel_label="Cancel" hide_close={true} submit_label="Confirm & Pay" onSubmit={props.isValid ? props.handleSubmit : undefined} submit_disabled={!props.isValid} onCancel={onClose} onClose={onClose} portal={true} is_submitting={props.isSubmitting}><Div m={{
              b: 4
            }}><PaymentElement onChange={event => props.setFieldValue("cc_complete", event.complete)} options={{
                layout: "tabs",
                paymentMethodOrder: ["card"]
              }} />{props.errors.cc_complete && <Text m={{
                t: 1
              }} danger={true}>{props.errors.cc_complete}</Text>}</Div><TextInput m={{
              b: 0
            }} name="email" type="email" label="Billing email" required={true} /></Modal></form>}</Formik>;
    };
    $[12] = handleSubmit;
    $[13] = onClose;
    $[14] = user.email;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t4 || $[17] !== t5) {
    t6 = <WrapWithStripeElement loading={t4}>{t5}</WrapWithStripeElement>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
const FeatureCell: React.FC<{
  text?: string | boolean;
  subtext?: string;
  billed?: boolean;
  details?: string;
}> = t0 => {
  const $ = _c(11);
  const {
    text,
    subtext,
    billed: t1,
    details
  } = t0;
  const billed = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      direction: "column",
      justify: "space-between"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== billed || $[2] !== details || $[3] !== subtext || $[4] !== text) {
    t3 = text && <><Div flex={{
        align: "center"
      }}><Icon primary={billed} muted={!billed} icon={billed ? "check" : "remove_done"} left={true} />{typeof text === "string" && <Text size="m">{text}</Text>}</Div>{subtext && (details ? <Tooltip tooltip={<Div>{details.split("\n").map(_temp6)}</Div>}><Text m={{
          l: 6
        }} muted={true} size="m" flex={{
          align: "center",
          gap: 1
        }}>{subtext} <Icon muted={true} icon="info" small={true} /></Text></Tooltip> : <Text m={{
        l: 6
      }} muted={true} size="m">{subtext}</Text>)}</>;
    $[1] = billed;
    $[2] = details;
    $[3] = subtext;
    $[4] = text;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <Div>{t3}</Div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Divider m={{
      t: 4
    }} />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <Div flex={t2} h={100}>{t4}{t5}</Div>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
const StyledFeatureRow = styled(Div)<{
  hightlight: boolean;
}>`
  ${({
  hightlight,
  theme
}) => hightlight && css`
      position: relative;
      z-index: 0;
      &:before {
        content: '';
        z-index: -1;
        position: absolute;
        top: ${theme.spacing(4)};
        left: ${theme.spacing(-2)};
        right: ${theme.spacing(-2)};
        bottom: ${theme.spacing(2)};
        border-radius: ${theme.radius.normal};
        background-color: ${theme.colors.surface.container.primary};
      }
    `}
`;
type FeatureRowCell = {
  text?: string | boolean;
  subtext?: string;
  billed?: boolean;
};
const FeatureRow: React.FC<{
  label: string;
  subtext?: string;
  hightlight: boolean;
  cells: (null | FeatureRowCell)[];
}> = t0 => {
  const $ = _c(18);
  const {
    label,
    subtext,
    cells,
    hightlight
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      t: 6
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      direction: "column",
      justify: "space-between"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <Text semi={true} size="m">{label}</Text>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== subtext) {
    t4 = subtext && <Text muted={true} size="m">{subtext}</Text>;
    $[4] = subtext;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Divider m={{
      t: 4
    }} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t4) {
    t6 = <GridUnit size={0.25}><Div flex={t2} h={100}>{t3}{t4}{t5}</Div></GridUnit>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== cells) {
    t7 = cells.map(_temp7);
    $[10] = cells;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <Grid gap={4}>{t6}{t7}</Grid>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== hightlight || $[16] !== t8) {
    t9 = <StyledFeatureRow hightlight={hightlight} p={t1}>{t8}</StyledFeatureRow>;
    $[15] = hightlight;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};
const formatPlan = (plan: OrbPlan) => ({
  id: plan.external_plan_id,
  version: plan.version,
  name: plan.name,
  features: plan.metadata.features && plan.metadata.features.length > 0 ? plan.metadata.features.split(',') : [],
  metered_prices: plan.prices.filter(price => price.price_type === 'usage_price'),
  metadata: plan.metadata,
  fixed_price: Number(plan.prices.find(price => price.price_type === 'fixed_price' && price.fixed_price_quantity === 1)?.unit_config?.unit_amount || 0)
});
const OrganizationPlans: React.FC = () => {
  const $ = _c(31);
  const location = useLocation();
  const product = location.pathname.includes("/outpost/plans") ? "outpost" : "event_gateway";
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription,
    mutateSubscription,
    organization_role
  } = useContext(DashboardContext);
  const isAuthorized = organization_levels_by_role[organization_role] >= organization_levels_by_role.admin;
  const [show_legacy_plan, setShowLegacyPlan] = useState(true);
  const [selecting_plan, setSelectingPlan] = useState(null);
  const history = useHistory();
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const {
    query
  } = useSearchQuery();
  const highlighted_feature = query.highlight;
  let t0;
  if ($[0] !== HookdeckAPI || $[1] !== product) {
    t0 = () => product_configs[product].getPlans(HookdeckAPI);
    $[0] = HookdeckAPI;
    $[1] = product;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    data: plans
  } = useSWR(`${product}:plans`, t0);
  let t1;
  if ($[3] !== HookdeckAPI || $[4] !== product) {
    t1 = () => product_configs[product].getSubscriptionDetails(HookdeckAPI);
    $[3] = HookdeckAPI;
    $[4] = product;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    data: subscription_details
  } = useSWR(`${product}:subscription_details`, t1);
  let t2;
  if ($[6] !== HookdeckAPI || $[7] !== addToast || $[8] !== highlighted_feature || $[9] !== history || $[10] !== isAuthorized || $[11] !== mutateSubscription || $[12] !== plans || $[13] !== product || $[14] !== selecting_plan || $[15] !== showDialog || $[16] !== show_legacy_plan || $[17] !== subscription || $[18] !== subscription_details) {
    const formatted_plans = plans?.map(formatPlan);
    const sorted_plans = formatted_plans && formatted_plans.sort(_temp8);
    const subscription_state = product_configs[product].deriveSubscriptionState(subscription);
    const handlePostUpgrade = new_subscription => {
      const new_plan_index = formatted_plans.findIndex(p => p.name === product_configs[product].deriveSubscriptionState(new_subscription).plan);
      const new_plan = formatted_plans[new_plan_index];
      const current_plan_index = formatted_plans.findIndex(p_0 => p_0.name === subscription_state.plan);
      mutateSubscription(new_subscription);
      let upgrade_modal_fields;
      if (product_configs[product].post_upgrade_modal_fields && product_configs[product].post_upgrade_modal_fields[new_subscription.plan] && new_plan_index > current_plan_index) {
        upgrade_modal_fields = () => <><Text as="p" m={{
            b: 2
          }}>What was the main reasons for your upgrade?</Text><Div flex={{
            direction: "column",
            gap: 2
          }}>{product_configs[product].post_upgrade_modal_fields[new_subscription.plan].map(_temp9)}</Div></>;
      }
      if (upgrade_modal_fields) {
        showDialog(v => HookdeckAPI.track.event("Selected Upgrade Reasons", {
          reasons: v.reasons,
          plan: new_plan.id
        }).catch(_temp10), undefined, {
          title: "Plan upgraded successfully!",
          submit_label: "Submit",
          cancel_label: "Close",
          form_props: {
            initial_values: {
              reasons: {}
            },
            Fields: upgrade_modal_fields
          }
        });
      } else {
        addToast("success", "You have successfully subscribed to this plan.");
      }
      history.push("/settings/organization/billing");
    };
    const onPlanSelected = async plan => {
      const is_free_plan = subscription_state.plan.startsWith(product_configs[product].free_plan.code_name);
      if (plan.startsWith(product_configs[product].free_plan.code_name) && !is_free_plan) {
        return history.push({
          pathname: `${product_configs[product].base_path}/downgrade`,
          state: {
            scroll: false
          }
        });
      }
      let has_card = true;
      if (is_free_plan) {
        setSelectingPlan(plan);
        has_card = await HookdeckAPI.billing.getCard().then(_temp11).catch(_temp12);
        setSelectingPlan(null);
      }
      history.push({
        pathname: has_card ? `${product_configs[product].base_path}/confirm/${plan}` : `${product_configs[product].base_path}/${plan}`,
        state: {
          scroll: false
        }
      });
    };
    const current_plan_index_0 = sorted_plans?.findIndex(p_1 => subscription_state.plan && subscription_state.plan.startsWith(p_1.id));
    const outdated_version = subscription_details && sorted_plans && !subscription_state.cancel_at && (current_plan_index_0 < 0 || current_plan_index_0 >= 0 && sorted_plans && sorted_plans[current_plan_index_0].version !== subscription_details.plan.version);
    const is_enterprise_plan = subscription_state.plan.includes("enterprise_") || subscription_state.plan.includes("custom_");
    const billing_metrics = {
      requests: false,
      events: false,
      discarded_requests: false,
      attempts: false,
      outpost_events: false
    };
    const getPlanMetric = (plan_0, metric) => {
      const prices = plan_0.metered_prices || plan_0.prices;
      const metered_price = prices.find(price => price.name.toLowerCase() === metric.toLowerCase());
      if (!metered_price) {
        return {
          text: "N/A",
          subtext: "Not billed on this plan",
          billed: false
        };
      }
      bb116: switch (metric) {
        case "events":
          {
            billing_metrics.events = true;
            break bb116;
          }
        case "requests":
          {
            billing_metrics.requests = true;
            break bb116;
          }
        case "discarded requests":
          {
            billing_metrics.discarded_requests = true;
            break bb116;
          }
        case "attempts":
          {
            billing_metrics.attempts = true;
            break bb116;
          }
        case "outpost events":
          {
            billing_metrics.outpost_events = true;
          }
      }
      const allocation = metered_price?.package_with_allocation_config?.allocation || subscription?.max_requests || plan_0.metadata?.max_requests || 10000;
      const is_free_plan_0 = product_configs[product].free_plan.code_name === plan_0.id || plan_0.fixed_price === 0;
      if (product === "event_gateway" && is_free_plan_0) {
        return {
          text: `Includes ${Number(allocation).toLocaleString("en-US")}`,
          subtext: "Overage not available",
          billed: true
        };
      }
      if ("package_with_allocation_config" in metered_price) {
        const scale = 1000000 / Number(metered_price.package_with_allocation_config.package_size);
        const price_per_million = Number(scale * Number(metered_price.package_with_allocation_config.package_amount));
        return {
          text: `Includes ${Number(allocation).toLocaleString("en-US")}`,
          subtext: `+ $${price_per_million} per 1 million`,
          billed: true
        };
      } else {
        if ("tiered_package_config" in metered_price) {
          const formatTierPrice = per_unit => {
            const price_0 = Number(per_unit) * (1000000 / Number(metered_price.tiered_package_config.package_size));
            return `$${Number(price_0.toFixed(1)).toString()}`;
          };
          const formatTierBound = _temp14;
          const getTierString = tiers => tiers.map((tier, index) => {
            const nextTier = tiers[index + 1];
            const lowerBound = formatTierBound(tier.tier_lower_bound);
            const upperBound = nextTier ? formatTierBound(nextTier.tier_lower_bound) : "+";
            const price_label = parseFloat(tier.per_unit) === 0 ? "Included" : `${formatTierPrice(tier.per_unit)} per million`;
            return `${lowerBound}${upperBound === "+" ? "" : `-${upperBound}`}: ${price_label}`;
          }).join("\n");
          return {
            text: `Includes ${Number(metered_price.tiered_package_config.tiers[1].tier_lower_bound).toLocaleString("en-US")}`,
            subtext: "Tiered pricing",
            details: getTierString(metered_price.tiered_package_config.tiers),
            billed: true
          };
        }
      }
      Sentry.captureException(new Error("Invalid plan pricing"), {
        extra: {
          plan: plan_0,
          prices,
          metered_price,
          is_free_plan: is_free_plan_0
        }
      });
      return {} as FeatureRowCell;
    };
    const {
      plansEventsRow,
      plansDiscardedRequestRow,
      plansRequestsRow,
      plansAttemptsRow,
      plansOutpostEventsRow
    } = (sorted_plans || []).reduce((acc, orbPlan) => {
      const is_current_plan = subscription_details?.plan?.external_plan_id && subscription_details?.plan?.external_plan_id.startsWith(orbPlan.id) || subscription_details?.plan?.external_plan_id && subscription_details?.plan?.external_plan_id.startsWith("business") && orbPlan.id.startsWith("team");
      const plan_1 = is_current_plan && show_legacy_plan ? formatPlan(subscription_details.plan) : orbPlan;
      acc.plansEventsRow.push(getPlanMetric(plan_1, "events"));
      acc.plansDiscardedRequestRow.push(getPlanMetric(plan_1, "discarded requests"));
      acc.plansRequestsRow.push(getPlanMetric(plan_1, "requests"));
      acc.plansAttemptsRow.push(getPlanMetric(plan_1, "attempts"));
      acc.plansOutpostEventsRow.push(getPlanMetric(plan_1, "outpost events"));
      return acc;
    }, {
      plansEventsRow: [] as FeatureRowCell[],
      plansDiscardedRequestRow: [] as FeatureRowCell[],
      plansRequestsRow: [] as FeatureRowCell[],
      plansAttemptsRow: [] as FeatureRowCell[],
      plansOutpostEventsRow: [] as FeatureRowCell[]
    });
    const getPlanFeature = (plan_index, metadata, feature) => {
      const is_current_plan_0 = current_plan_index_0 === plan_index;
      const current_subscription_metadata = product === "outpost" ? subscription?.outpost_config : subscription;
      return is_current_plan_0 && show_legacy_plan ? current_subscription_metadata?.[feature] || subscription_details?.plan?.metadata?.[feature] || metadata[feature] : metadata[feature];
    };
    let t3;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        b: 20
      };
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    let t4;
    let t5;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        justify: "space-between",
        align: "center",
        gap: 4
      };
      t5 = {
        b: 4
      };
      $[21] = t4;
      $[22] = t5;
    } else {
      t4 = $[21];
      t5 = $[22];
    }
    const t6 = product_configs[product];
    let t7;
    if ($[23] !== t6.title) {
      t7 = <Text bold={true} size="l" as="h1" m={0}>{t6.title}</Text>;
      $[23] = t6.title;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    let t8;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Text muted={true} as="p" m={0}>Plans apply across every project in an organization. If you need help,{" "}<Link as="button" primary={true} onClick={showChat}>contact us.</Link></Text>;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    let t9;
    if ($[26] !== t7) {
      t9 = <Div>{t7}{t8}</Div>;
      $[26] = t7;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    let t10;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        gap: 4,
        align: "center"
      };
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    let t11;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Button outline={true} as="a" href={LINKS.pricing} target="_blank" icon="link">Full plan breakdown</Button>;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Divider m={{
        b: 8
      }} />;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    t2 = <Container xlarge={true} p={t3}>{isAuthorized && <SwitchRoute><Route path={`${product_configs[product].base_path}/downgrade`} render={() => <CancelModal product={product} />} /><Route path={`${product_configs[product].base_path}/confirm/:plan`} render={() => <ChangePlanConfirmationModal product={product} handlePostUpgrade={handlePostUpgrade} />} /><Route path={`${product_configs[product].base_path}/:plan`} render={() => <PaymentMethodModal product={product} handlePostUpgrade={handlePostUpgrade} />} /></SwitchRoute>}<Div flex={t4} m={t5}>{t9}<Div flex={t10}>{outdated_version && current_plan_index_0 !== -1 && <Tooltip tooltip="We no longer offer the plan you're currently subscribed to. Hookdeck honors the usage and features offered by your legacy plan."><Button outline={true} flex={{
              gap: 2,
              align: "center"
            }} onClick={() => setShowLegacyPlan(!show_legacy_plan)}><Switch checked={show_legacy_plan} onChange={e => setShowLegacyPlan(e.target.checked)} /><Text semi={true}>Show legacy plan</Text><Icon muted={true} pointer={true} icon="info" /></Button></Tooltip>}{t11}</Div></Div>{t12}{is_enterprise_plan || current_plan_index_0 === -1 ? <Alert info={true} inline={true}>You are currently on a enterprise or custom plan, contact us to change your plan.</Alert> : <Div flex={{
        justify: "center",
        direction: "column"
      }}>{!sorted_plans || !subscription_details ? <Div p={8} flex={{
          justify: "center"
        }}><Loading /></Div> : <>{subscription_state.cancel_at && new Date(subscription_state.cancel_at).getTime() > Date.now() && <Alert warning={true} inline={true} m={{
            b: 6
          }}>You've previously cancelled your paid plan and have{" "}{differenceInDays(new Date(subscription_state.cancel_at), new Date())} days left until you will be downgraded to the {product_configs[product].free_plan.name}{" "}(Free) Plan.</Alert>}<Grid gap={4}><GridUnit size={0.25} />{sorted_plans.map((orb_plan, i) => {
              let plan_2 = orb_plan;
              let price_1 = orb_plan.fixed_price;
              let is_current_plan_1 = current_plan_index_0 === i;
              const is_canceled = subscription_state.cancel_at && new Date(subscription_state.cancel_at).getTime() > Date.now();
              if (show_legacy_plan && current_plan_index_0 === i && !is_canceled) {
                plan_2 = formatPlan(subscription_details.plan);
                price_1 = plan_2.fixed_price;
              }
              if (is_canceled) {
                is_current_plan_1 = orb_plan.id.startsWith(product_configs[product].free_plan.code_name);
              }
              const plan_name = plan_2.name.split(" ")[0];
              return <GridUnit size={0.25} key={plan_2.id}><Div><Div flex={{
                    align: "center",
                    gap: 2
                  }}><Text size="l">{plan_name}</Text>{is_current_plan_1 && show_legacy_plan && outdated_version && <Badge mono={true} neutral={true}>Legacy</Badge>}</Div><Text bold={true} size="l" capitalize={true} as="p" m={{
                    b: 4
                  }}>{price_1 === 0 ? "Free" : `$${price_1} / month`}</Text><Button.Permission role="admin" block={true} disabled={!!selecting_plan || is_current_plan_1 && !(outdated_version && !show_legacy_plan)} icon={selecting_plan === plan_2.id ? "loading" : undefined} onClick={() => onPlanSelected(plan_2.id)} primary={!!current_plan_index_0 && i > current_plan_index_0 || is_current_plan_1 && outdated_version && !show_legacy_plan} outline={!!current_plan_index_0 && i < current_plan_index_0 || is_current_plan_1 && outdated_version}>{is_current_plan_1 ? outdated_version && !show_legacy_plan ? "Update plan" : "Current plan" : current_plan_index_0 && i < current_plan_index_0 ? "Downgrade" : "Upgrade"}</Button.Permission></Div></GridUnit>;
            })}</Grid><Divider m={{
            y: 8
          }} /><Text muted={true} size="l">Usage</Text>{billing_metrics.events && <FeatureRow label="Events" hightlight={highlighted_feature === "events"} cells={plansEventsRow} />}{billing_metrics.discarded_requests && <FeatureRow label="Discarded Requests" hightlight={highlighted_feature === "request"} cells={plansDiscardedRequestRow} />}{billing_metrics.requests && <FeatureRow label="Requests" hightlight={highlighted_feature === "request"} cells={plansRequestsRow} />}{billing_metrics.attempts && <FeatureRow label="Attempts" hightlight={highlighted_feature === "request"} cells={plansAttemptsRow} />}{billing_metrics.outpost_events && <FeatureRow label="Outpost Events" hightlight={highlighted_feature === "outpost_events"} cells={plansOutpostEventsRow} />}<Text muted={true} size="l" m={{
            t: 8
          }}>Features</Text>{product_configs[product].features.map(feature_0 => <FeatureRow key={feature_0.key} label={feature_0.label} hightlight={highlighted_feature === feature_0.key} cells={sorted_plans.map((t13, i_0) => {
            const {
              metadata: metadata_0,
              features
            } = t13;
            const feature_value = getPlanFeature(i_0, metadata_0, feature_0.key);
            return {
              text: feature_0.format(feature_value, features)
            };
          })} />)}</>}</Div>}</Container>;
    $[6] = HookdeckAPI;
    $[7] = addToast;
    $[8] = highlighted_feature;
    $[9] = history;
    $[10] = isAuthorized;
    $[11] = mutateSubscription;
    $[12] = plans;
    $[13] = product;
    $[14] = selecting_plan;
    $[15] = showDialog;
    $[16] = show_legacy_plan;
    $[17] = subscription;
    $[18] = subscription_details;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  return t2;
};
export default OrganizationPlans;
function _temp(t) {
  return t.static_ip_proxy;
}
function _temp2(t_0) {
  return t_0.name;
}
function _temp3(resolve) {
  return setTimeout(resolve, 2000);
}
function _temp4(resolve_0) {
  return setTimeout(resolve_0, 5000);
}
function _temp5(values_1) {
  const errors = {};
  if (!values_1.email || values_1.email.length < 1) {
    errors.email = "Email is required";
  }
  if (!values_1.cc_complete) {
    errors.cc_complete = "Credit card is required";
  }
  return errors;
}
function _temp6(line, index) {
  return <Text key={index} align="left">{line}</Text>;
}
function _temp7(cell, i) {
  return <GridUnit size={0.25} key={i}><FeatureCell {...cell} /></GridUnit>;
}
function _temp8(a, b) {
  return a.fixed_price - b.fixed_price;
}
function _temp9(field) {
  return <CheckboxInput key={field.name} name={`reasons.${field.name}`} label={field.label} />;
}
function _temp10(err) {
  Sentry.captureException(err);
}
function _temp11(card) {
  return !!card;
}
function _temp12() {
  return true;
}
function _temp13(value) {
  return Number(value.toFixed(1)).toString();
}
function _temp14(bound) {
  const num = Number(bound);
  const round = _temp13;
  if (num >= 1000000000) {
    return `${round(num / 1000000000)}B`;
  }
  if (num >= 1000000) {
    return `${round(num / 1000000)}M`;
  }
  if (num >= 1000) {
    return `${round(num / 1000)}k`;
  }
  return num.toString();
}